import Swal from 'sweetalert2';

/**
 * Tenant suspension (entitlements S1).
 *
 * When an organisation is suspended, cancelled or pending deletion, the Go
 * backend answers EVERY authenticated request (and every login) with
 *   403 {"statusCode":403,"message":"Tenant is suspended","error":"Forbidden"}
 * This message is the contract; see backend-go/internal/shared/tenant/status.go.
 *
 * Without special handling a user who still holds a session would see the
 * generic "Permission Denied" toast on every call until the 15-minute token
 * check logs them out. Instead: tell them once, clear the session, and send
 * them to the login page.
 */
export const TENANT_SUSPENDED_MESSAGE = 'Tenant is suspended';
export const TENANT_SUSPENDED_NOTICE = "Your organisation's access is suspended";

export const isTenantSuspendedError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } } | null)?.response;
  return response?.status === 403 && response?.data?.message === TENANT_SUSPENDED_MESSAGE;
};

let handled = false;

/** Same clearing as authSlice's clearAllSession (kept local to avoid an import cycle). */
const clearSession = () => {
  sessionStorage.clear();
  document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

/**
 * Clear the session and show the suspension notice exactly once per page
 * load. With `redirect`, the browser then goes to /login (a full reload, so
 * no stale in-memory state survives); without it the caller navigates.
 */
export const handleTenantSuspended = (redirect: boolean): void => {
  clearSession();
  if (handled) {
    return;
  }
  handled = true;
  const shown = Swal.fire({
    icon: 'warning',
    title: TENANT_SUSPENDED_NOTICE,
    text: 'Please contact your administrator.',
    timer: 6_000,
    timerProgressBar: true,
  });
  // Never redirect from a login page: an HttpOnly SSO cookie (which JS cannot
  // clear) would draw the same 403 again after the reload, and redirecting
  // there would loop.
  const onLoginPage = /\/login(\/|$)/.test(window.location.pathname);
  if (redirect && !onLoginPage) {
    shown.finally(() => {
      window.location.assign('/login');
    });
  }
};
